import { Capacitor, registerPlugin } from "@capacitor/core";

export type GameOrientation = "landscape" | "portrait";
export type OrientationResult = "applied" | "manual" | "desktop";
export const ORIENTATION_STORAGE_KEY = "varnax-screen-orientation";

interface NativeOrientationPlugin {
  getPreference(): Promise<{ orientation: GameOrientation }>;
  setOrientation(options: { orientation: GameOrientation }): Promise<void>;
}

const nativeOrientation = registerPlugin<NativeOrientationPlugin>("VarnaxOrientation");
let memoryPreference: GameOrientation = "landscape";
let preferenceRevision = 0;

export const parseGameOrientation = (value: unknown): GameOrientation | null =>
  value === "landscape" || value === "portrait" ? value : null;

function storedOrientation(): GameOrientation | null {
  try { return parseGameOrientation(localStorage.getItem(ORIENTATION_STORAGE_KEY)); }
  catch { return null; }
}

export function getGameOrientation(): GameOrientation {
  return storedOrientation() ?? memoryPreference;
}

export function saveGameOrientation(orientation: GameOrientation): void {
  preferenceRevision += 1;
  memoryPreference = orientation;
  try { localStorage.setItem(ORIENTATION_STORAGE_KEY, orientation); }
  catch { /* Keep the preference for this session when storage is unavailable. */ }
}

function isNativeAndroid(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === "android";
}

/** The native activity applies its saved preference before creating the WebView. */
export async function initializeGameOrientation(): Promise<void> {
  if (!isNativeAndroid()) return;
  const revision = preferenceRevision;
  try {
    const preference = storedOrientation()
      ?? parseGameOrientation((await nativeOrientation.getPreference()).orientation)
      ?? "landscape";
    if (revision !== preferenceRevision) return;
    saveGameOrientation(preference);
    await nativeOrientation.setOrientation({ orientation: preference });
  } catch {
    // A web update may be opened by an older Android binary without this plugin.
    // Keep the game playable in the current orientation.
  }
}

export interface BrowserOrientationControls {
  mobile: boolean;
  fullscreen: boolean;
  requestFullscreen?: (() => Promise<void>) | undefined;
  lock?: ((orientation: GameOrientation) => Promise<void>) | undefined;
}

/** Must be invoked synchronously from Play or a settings button, before awaiting a network call. */
export async function applyBrowserOrientation(
  orientation: GameOrientation,
  controls: BrowserOrientationControls,
): Promise<OrientationResult> {
  if (!controls.mobile) return "desktop";
  if (!controls.lock) return "manual";
  try {
    // Android browsers require fullscreen for orientation.lock. Requesting it
    // here retains the activation from the player's actual button press.
    if (!controls.fullscreen && controls.requestFullscreen) await controls.requestFullscreen();
    await controls.lock(orientation);
    return "applied";
  } catch {
    // Safari, multi-window mode and browser policy may refuse a lock. The
    // responsive game remains playable and the saved preference is retained.
    return "manual";
  }
}

export async function requestGameOrientation(): Promise<OrientationResult> {
  const orientation = getGameOrientation();
  if (isNativeAndroid()) {
    try {
      await nativeOrientation.setOrientation({ orientation });
      return "applied";
    } catch { return "manual"; }
  }
  const screenOrientation = window.screen.orientation as (ScreenOrientation & {
    lock?: (value: GameOrientation) => Promise<void>;
  }) | undefined;
  return applyBrowserOrientation(orientation, {
    mobile: window.matchMedia("(any-pointer: coarse)").matches && navigator.maxTouchPoints > 0,
    fullscreen: Boolean(document.fullscreenElement),
    requestFullscreen: document.documentElement.requestFullscreen
      ? () => document.documentElement.requestFullscreen({ navigationUI: "hide" })
      : undefined,
    lock: screenOrientation?.lock ? (value) => screenOrientation.lock!(value) : undefined,
  });
}
